// Check-In / Check-Out Service - Mock Data & Business Logic

import type {
  CheckOutRecord,
  Reservation,
  CheckInOutDashboardStats,
  CheckInRequest,
  CheckOutRequest,
  CheckInOutSettings,
  CalendarEvent,
  ConflictResolution,
  CheckInOutFilter,
  CalendarView,
  CheckInOutAuditEntry,
} from '@/types/checkinout';

// Mock data
const mockCheckOutRecords: CheckOutRecord[] = [
  {
    id: 'co-001',
    assetId: 'VEH-001',
    assetName: 'Ford Transit Van - FX67 ABC',
    assetCategory: 'Vehicles',
    assignedTo: 'user-001',
    assignedToName: 'John Smith',
    assignedToTeam: 'Field Operations',
    checkOutDate: '2024-01-10T09:00:00Z',
    expectedReturnDate: '2024-01-15T17:00:00Z',
    dueInDays: -3,
    checkOutLocation: 'Manchester Depot',
    currentLocation: 'Birmingham Site',
    locationType: 'site',
    status: 'overdue',
    condition: 'good',
    purpose: 'Equipment delivery to Birmingham project',
    notes: 'Vehicle has full tank',
    accessories: [
      { id: 'acc-001', name: 'Fuel Card', quantity: 1, returned: false },
      { id: 'acc-002', name: 'Sat Nav', quantity: 1, returned: false },
    ],
    complianceStatus: 'compliant',
    checkedOutBy: 'user-001',
    createdDate: '2024-01-10T09:00:00Z',
  },
  {
    id: 'co-002',
    assetId: 'TOOL-045',
    assetName: 'Hilti Hammer Drill TE 3000-AVR',
    assetCategory: 'Tools',
    assignedTo: 'user-002',
    assignedToName: 'Sarah Johnson',
    assignedToTeam: 'Construction',
    checkOutDate: '2024-01-12T08:30:00Z',
    expectedReturnDate: '2024-01-18T16:00:00Z',
    dueInDays: 0,
    checkOutLocation: 'London Warehouse',
    currentLocation: 'London Warehouse',
    locationType: 'site',
    status: 'due-soon',
    condition: 'excellent',
    purpose: 'Foundation work',
    accessories: [
      { id: 'acc-003', name: 'Drill Bits Set', quantity: 1, returned: false },
      { id: 'acc-004', name: 'Safety Goggles', quantity: 1, returned: false },
      { id: 'acc-005', name: 'Carry Case', quantity: 1, returned: false },
    ],
    complianceStatus: 'compliant',
    checkedOutBy: 'user-002',
    createdDate: '2024-01-12T08:30:00Z',
  },
  {
    id: 'co-003',
    assetId: 'LIFT-012',
    assetName: 'Genie GS-2632 Scissor Lift',
    assetCategory: 'Lifting Equipment',
    assignedTo: 'user-003',
    assignedToName: 'Michael Brown',
    assignedToTeam: 'Maintenance',
    checkOutDate: '2024-01-08T07:00:00Z',
    expectedReturnDate: '2024-01-12T18:00:00Z',
    dueInDays: -6,
    checkOutLocation: 'Leeds Depot',
    currentLocation: 'In Transit',
    locationType: 'in-transit',
    status: 'overdue',
    condition: 'good',
    purpose: 'Ceiling installation work',
    notes: 'Requires inspection before next use',
    complianceStatus: 'warning',
    complianceIssues: ['Annual inspection due in 15 days'],
    checkedOutBy: 'user-003',
    createdDate: '2024-01-08T07:00:00Z',
  },
  {
    id: 'co-004',
    assetId: 'LAPTOP-089',
    assetName: 'Dell Latitude 5420 Laptop',
    assetCategory: 'Computer Equipment',
    assignedTo: 'user-004',
    assignedToName: 'Emma Wilson',
    assignedToTeam: 'Project Management',
    checkOutDate: '2024-01-14T10:00:00Z',
    expectedReturnDate: '2024-01-21T17:00:00Z',
    dueInDays: 3,
    checkOutLocation: 'Head Office',
    currentLocation: 'Remote - Home Office',
    locationType: 'remote',
    status: 'checked-out',
    condition: 'excellent',
    purpose: 'Remote working',
    accessories: [
      { id: 'acc-006', name: 'Laptop Charger', quantity: 1, returned: false },
      { id: 'acc-007', name: 'Mouse', quantity: 1, returned: false },
      { id: 'acc-008', name: 'Laptop Bag', quantity: 1, returned: false },
    ],
    complianceStatus: 'compliant',
    checkedOutBy: 'user-004',
    createdDate: '2024-01-14T10:00:00Z',
  },
  {
    id: 'co-005',
    assetId: 'FURN-234',
    assetName: 'Herman Miller Aeron Chair',
    assetCategory: 'Furniture',
    assignedTo: 'user-005',
    assignedToName: 'David Lee',
    assignedToTeam: 'IT Department',
    checkOutDate: '2024-01-15T09:00:00Z',
    expectedReturnDate: '2024-01-22T17:00:00Z',
    dueInDays: 4,
    checkOutLocation: 'Head Office',
    currentLocation: 'Remote - Home Office',
    locationType: 'remote',
    status: 'checked-out',
    condition: 'good',
    purpose: 'Home office setup',
    complianceStatus: 'compliant',
    checkedOutBy: 'user-005',
    createdDate: '2024-01-15T09:00:00Z',
  },
];

const mockReservations: Reservation[] = [
  {
    id: 'res-001',
    assetId: 'VEH-002',
    assetName: 'Mercedes Sprinter Van - GH19 XYZ',
    assetCategory: 'Vehicles',
    reservedFor: 'user-006',
    reservedForName: 'Lisa Anderson',
    reservedForTeam: 'Logistics',
    startDate: '2024-01-20T08:00:00Z',
    endDate: '2024-01-25T17:00:00Z',
    createdDate: '2024-01-10T14:30:00Z',
    preferredLocation: 'Manchester Depot',
    status: 'confirmed',
    purpose: 'Equipment transport to Scotland',
    createdBy: 'user-006',
  },
  {
    id: 'res-002',
    categoryId: 'cat-tools',
    categoryName: 'Power Tools',
    reservedFor: 'user-007',
    reservedForName: 'Tom Harris',
    reservedForTeam: 'Construction',
    startDate: '2024-01-19T07:00:00Z',
    endDate: '2024-01-21T18:00:00Z',
    createdDate: '2024-01-11T10:00:00Z',
    preferredLocation: 'London Warehouse',
    status: 'pending',
    purpose: 'Renovation project',
    hasConflict: true,
    conflictingReservations: ['res-003'],
    createdBy: 'user-007',
  },
  {
    id: 'res-003',
    assetId: 'LIFT-015',
    assetName: 'JLG 600S Boom Lift',
    assetCategory: 'Lifting Equipment',
    reservedFor: 'user-008',
    reservedForName: 'Rachel Green',
    reservedForTeam: 'Facilities',
    startDate: '2024-01-22T08:00:00Z',
    endDate: '2024-01-26T17:00:00Z',
    createdDate: '2024-01-12T11:20:00Z',
    preferredLocation: 'Birmingham Depot',
    status: 'confirmed',
    purpose: 'Building maintenance',
    createdBy: 'user-008',
  },
];

const mockSettings: CheckInOutSettings = {
  reminderEnabled: true,
  reminderDaysBefore: 2,
  reminderFrequency: 'daily',
  gracePeriodEnabled: true,
  gracePeriodDays: 1,
  escalationEnabled: true,
  escalationDaysOverdue: 3,
  escalationRecipients: ['manager@company.com', 'operations@company.com'],
  complianceGateEnabled: true,
  complianceGateAction: 'warn',
  blockNonCompliantCheckOut: false,
  kioskModeEnabled: false,
  kioskAutoLogoutMinutes: 5,
  notifyOnCheckOut: true,
  notifyOnCheckIn: true,
  notifyOnOverdue: true,
  defaultCheckOutDays: 7,
  requireAccessoryCheck: true,
  requirePhotos: false,
  requireConditionReport: true,
};

// Service functions
export const checkInOutService = {
  // Dashboard
  async getDashboardStats(): Promise<CheckInOutDashboardStats> {
    await delay(300);
    
    const checkedOut = mockCheckOutRecords.filter(r => !r.actualReturnDate);
    const overdue = checkedOut.filter(r => r.status === 'overdue');
    const dueToday = checkedOut.filter(r => r.dueInDays === 0);
    const inTransit = checkedOut.filter(r => r.locationType === 'in-transit');
    
    return {
      totalCheckedOut: checkedOut.length,
      dueToday: dueToday.length,
      overdue: overdue.length,
      inTransit: inTransit.length,
      checkedOutChange: 5,
      overdueChange: -2,
      topDemandAssets: [
        {
          assetId: 'VEH-001',
          assetName: 'Ford Transit Van - FX67 ABC',
          category: 'Vehicles',
          checkOutCount: 45,
          averageDuration: 5.2,
          utilizationRate: 85,
        },
        {
          assetId: 'TOOL-045',
          assetName: 'Hilti Hammer Drill TE 3000-AVR',
          category: 'Tools',
          checkOutCount: 38,
          averageDuration: 3.5,
          utilizationRate: 72,
        },
        {
          assetId: 'LIFT-012',
          assetName: 'Genie GS-2632 Scissor Lift',
          category: 'Lifting Equipment',
          checkOutCount: 32,
          averageDuration: 6.8,
          utilizationRate: 68,
        },
      ],
      idleAssets: [
        {
          assetId: 'FURN-456',
          assetName: 'Conference Table - Oak',
          category: 'Furniture',
          lastCheckOutDate: '2023-09-15T10:00:00Z',
          daysSinceLastUse: 125,
          location: 'Head Office Storage',
        },
        {
          assetId: 'ELEC-789',
          assetName: 'Generator - Honda EU22i',
          category: 'Electrical Equipment',
          lastCheckOutDate: '2023-10-20T08:00:00Z',
          daysSinceLastUse: 90,
          location: 'Manchester Depot',
        },
      ],
      overdueList: overdue,
    };
  },

  // Check-Out Records
  async getCheckOutRecords(filter?: CheckInOutFilter): Promise<CheckOutRecord[]> {
    await delay(300);
    let records = [...mockCheckOutRecords];
    
    if (filter) {
      if (filter.status && filter.status.length > 0) {
        records = records.filter(r => filter.status!.includes(r.status));
      }
      if (filter.location && filter.location.length > 0) {
        records = records.filter(r => filter.location!.includes(r.currentLocation));
      }
      if (filter.assignedTo && filter.assignedTo.length > 0) {
        records = records.filter(r => filter.assignedTo!.includes(r.assignedTo));
      }
      if (filter.category && filter.category.length > 0) {
        records = records.filter(r => filter.category!.includes(r.assetCategory));
      }
      if (filter.searchTerm) {
        const term = filter.searchTerm.toLowerCase();
        records = records.filter(r =>
          r.assetName.toLowerCase().includes(term) ||
          r.assignedToName.toLowerCase().includes(term) ||
          r.assetId.toLowerCase().includes(term)
        );
      }
    }
    
    return records;
  },

  async getCheckOutRecord(id: string): Promise<CheckOutRecord | null> {
    await delay(200);
    return mockCheckOutRecords.find(r => r.id === id) ?? null;
  },

  async checkOutAsset(request: CheckOutRequest): Promise<CheckOutRecord> {
    await delay(500);
    
    const newRecord: CheckOutRecord = {
      id: `co-${Date.now()}`,
      assetId: request.assetId,
      assetName: 'Asset Name', // Would be fetched from asset service
      assetCategory: 'Category', // Would be fetched from asset service
      assignedTo: request.assignedTo,
      assignedToName: 'User Name', // Would be fetched from user service
      assignedToTeam: request.assignedToTeam,
      checkOutDate: request.checkOutDate,
      expectedReturnDate: request.expectedReturnDate,
      dueInDays: calculateDueInDays(request.expectedReturnDate),
      checkOutLocation: request.location,
      currentLocation: request.location,
      locationType: request.locationType,
      status: 'checked-out',
      purpose: request.purpose,
      notes: request.notes,
      accessories: request.accessories?.map((acc, idx) => ({
        id: `acc-${Date.now()}-${idx}`,
        ...acc,
        returned: false,
      })),
      complianceStatus: 'compliant',
      checkedOutBy: 'current-user',
      createdDate: new Date().toISOString(),
    };
    
    mockCheckOutRecords.push(newRecord);
    return newRecord;
  },

  async checkInAsset(request: CheckInRequest): Promise<CheckOutRecord> {
    await delay(500);
    
    const record = mockCheckOutRecords.find(r => r.id === request.checkOutRecordId);
    if (!record) {
      throw new Error('Check-out record not found');
    }
    
    record.actualReturnDate = request.checkInDate;
    record.condition = request.condition;
    record.currentLocation = request.finalLocation;
    record.locationType = request.locationType;
    record.status = 'available';
    record.checkedInBy = 'current-user';
    record.modifiedDate = new Date().toISOString();
    
    if (request.notes) {
      record.notes = (record.notes ?? '') + '\n\nCheck-in notes: ' + request.notes;
    }
    
    if (request.accessories) {
      record.accessories = request.accessories;
    }
    
    return record;
  },

  // Reservations
  async getReservations(filter?: CheckInOutFilter): Promise<Reservation[]> {
    await delay(300);
    let reservations = [...mockReservations];
    
    if (filter?.searchTerm) {
      const term = filter.searchTerm.toLowerCase();
      reservations = reservations.filter(r =>
        r.assetName?.toLowerCase().includes(term) ||
        r.reservedForName.toLowerCase().includes(term) ||
        r.categoryName?.toLowerCase().includes(term)
      );
    }
    
    return reservations;
  },

  async createReservation(reservation: Omit<Reservation, 'id' | 'createdDate'>): Promise<Reservation> {
    await delay(500);
    
    const newReservation: Reservation = {
      ...reservation,
      id: `res-${Date.now()}`,
      createdDate: new Date().toISOString(),
    };
    
    mockReservations.push(newReservation);
    return newReservation;
  },

  async cancelReservation(id: string, reason: string): Promise<void> {
    await delay(300);
    
    const reservation = mockReservations.find(r => r.id === id);
    if (reservation) {
      reservation.status = 'cancelled';
      reservation.cancelledDate = new Date().toISOString();
      reservation.cancelledBy = 'current-user';
      reservation.cancellationReason = reason;
    }
  },

  async checkConflicts(
    assetId: string,
    startDate: string,
    endDate: string
  ): Promise<ConflictResolution> {
    await delay(300);
    
    // Simple conflict detection
    const conflicts = mockReservations.filter(r =>
      r.assetId === assetId &&
      r.status !== 'cancelled' &&
      ((r.startDate >= startDate && r.startDate <= endDate) ||
       (r.endDate >= startDate && r.endDate <= endDate))
    );
    
    return {
      hasConflict: conflicts.length > 0,
      conflictingReservations: conflicts,
      suggestedAlternatives: [
        {
          assetId: 'ALT-001',
          assetName: 'Alternative Asset 1',
          category: 'Vehicles',
          location: 'Manchester Depot',
          availableFrom: startDate,
          availableTo: endDate,
          matchScore: 95,
        },
      ],
    };
  },

  // Calendar
  async getCalendarEvents(view: CalendarView): Promise<CalendarEvent[]> {
    await delay(300);
    
    const events: CalendarEvent[] = [];
    
    // Add check-out events
    mockCheckOutRecords.forEach(record => {
      events.push({
        id: record.id,
        assetId: record.assetId,
        assetName: record.assetName,
        category: record.assetCategory,
        startDate: record.checkOutDate,
        endDate: record.actualReturnDate || record.expectedReturnDate,
        status: record.status,
        assignedTo: record.assignedTo,
        assignedToName: record.assignedToName,
        type: 'checkout',
      });
    });
    
    // Add reservation events
    mockReservations.forEach(reservation => {
      if (reservation.status !== 'cancelled') {
        events.push({
          id: reservation.id,
          assetId: reservation.assetId ?? '',
          assetName: reservation.assetName ?? reservation.categoryName ?? '',
          category: reservation.assetCategory ?? '',
          startDate: reservation.startDate,
          endDate: reservation.endDate,
          status: reservation.status,
          assignedTo: reservation.reservedFor,
          assignedToName: reservation.reservedForName,
          type: 'reservation',
        });
      }
    });
    
    return events;
  },

  // Settings
  async getSettings(): Promise<CheckInOutSettings> {
    await delay(200);
    return { ...mockSettings };
  },

  async updateSettings(settings: Partial<CheckInOutSettings>): Promise<CheckInOutSettings> {
    await delay(300);
    Object.assign(mockSettings, settings);
    return { ...mockSettings };
  },

  // Quick Actions
  async sendReminder(checkOutRecordId: string): Promise<void> {
    await delay(500);
    console.log(`Reminder sent for check-out record: ${checkOutRecordId}`);
  },

  async escalateOverdue(checkOutRecordId: string): Promise<void> {
    await delay(500);
    console.log(`Escalation sent for check-out record: ${checkOutRecordId}`);
  },

  async scheduleCollection(checkOutRecordId: string, collectionDate: string): Promise<void> {
    await delay(500);
    console.log(`Collection scheduled for ${collectionDate} for record: ${checkOutRecordId}`);
  },

  async extendCheckOut(checkOutRecordId: string, newReturnDate: string): Promise<CheckOutRecord> {
    await delay(500);
    
    const record = mockCheckOutRecords.find(r => r.id === checkOutRecordId);
    if (!record) {
      throw new Error('Check-out record not found');
    }
    
    record.expectedReturnDate = newReturnDate;
    record.dueInDays = calculateDueInDays(newReturnDate);
    record.modifiedDate = new Date().toISOString();
    
    return record;
  },

  // Audit Trail
  async getAuditTrail(assetId?: string): Promise<CheckInOutAuditEntry[]> {
    await delay(300);
    
    return [
      {
        id: 'audit-001',
        timestamp: '2024-01-15T10:30:00Z',
        action: 'checkout',
        assetId: 'VEH-001',
        assetName: 'Ford Transit Van - FX67 ABC',
        performedBy: 'user-001',
        performedByName: 'John Smith',
        details: {
          expectedReturnDate: '2024-01-20T17:00:00Z',
          location: 'Manchester Depot',
        },
      },
      {
        id: 'audit-002',
        timestamp: '2024-01-14T14:20:00Z',
        action: 'reservation',
        assetId: 'LIFT-015',
        assetName: 'JLG 600S Boom Lift',
        performedBy: 'user-008',
        performedByName: 'Rachel Green',
        details: {
          startDate: '2024-01-22T08:00:00Z',
          endDate: '2024-01-26T17:00:00Z',
        },
      },
    ];
  },
};

// Helper functions
function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function calculateDueInDays(returnDate: string): number {
  const now = new Date();
  const due = new Date(returnDate);
  const diffTime = due.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
}

export default checkInOutService;
